import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Copy, Check, Pencil, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { Pagination } from '../components/ui/Pagination.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ShortLink {
  urlCode: string;
  originalUrl: string;
  shortUrl: string;
  createdAt: string;
}

interface ShortLinksResponse {
  items: ShortLink[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── ShortlinksPage ───────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

export function ShortlinksPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createUrl, setCreateUrl] = useState('');
  const [createSlug, setCreateSlug] = useState('');

  // Edit dialog
  const [editTarget, setEditTarget] = useState<ShortLink | null>(null);
  const [editUrl, setEditUrl] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<ShortLink | null>(null);

  // ── Query ──────────────────────────────────────────────────────────────────
  const { data, isLoading, isError, error } = useQuery<ShortLinksResponse>({
    queryKey: ['shortlinks', page],
    queryFn: () =>
      api.get(`/api/v1/shortlinks?page=${page}&pageSize=${PAGE_SIZE}`).then((r) => r.data),
    staleTime: 15_000,
  });

  const lygoNotConfigured =
    isError &&
    (() => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      return status === 503;
    })();

  const items = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  // ── Copy short URL ─────────────────────────────────────────────────────────
  function handleCopy(link: ShortLink) {
    navigator.clipboard.writeText(link.shortUrl).then(
      () => {
        setCopiedCode(link.urlCode);
        toast.success('URL copiato negli appunti');
        setTimeout(() => setCopiedCode(null), 2000);
      },
      () => toast.error('Impossibile copiare — copia manualmente dalla pagina'),
    );
  }

  // ── Create ─────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (payload: { originalUrl: string; urlCode?: string }) =>
      api.post('/api/v1/shortlinks', payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shortlinks'] });
      toast.success('Shortlink creato');
      setCreateOpen(false);
      setCreateUrl('');
      setCreateSlug('');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!createUrl.trim()) return;
    const payload: { originalUrl: string; urlCode?: string } = { originalUrl: createUrl.trim() };
    if (createSlug.trim()) payload.urlCode = createSlug.trim();
    createMutation.mutate(payload);
  }

  // ── Edit ───────────────────────────────────────────────────────────────────
  function openEdit(link: ShortLink) {
    setEditTarget(link);
    setEditUrl(link.originalUrl);
  }

  const editMutation = useMutation({
    mutationFn: ({ urlCode, originalUrl }: { urlCode: string; originalUrl: string }) =>
      api.patch(`/api/v1/shortlinks/${urlCode}`, { originalUrl }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shortlinks'] });
      toast.success('Shortlink aggiornato');
      setEditTarget(null);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function handleEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editTarget || !editUrl.trim()) return;
    editMutation.mutate({ urlCode: editTarget.urlCode, originalUrl: editUrl.trim() });
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (urlCode: string) =>
      api.delete(`/api/v1/shortlinks/${urlCode}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shortlinks'] });
      toast.success('Shortlink eliminato');
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Shortlinks"
        description="Gestisci i link brevi tramite lygo.it"
        action={
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 transition-colors"
          >
            <Link2 size={15} />
            Nuovo shortlink
          </button>
        }
      />

      {/* Lygo not configured */}
      {lygoNotConfigured && (
        <div className="flex flex-col items-center justify-center py-24 text-zinc-400 gap-4">
          <Link2 size={48} className="opacity-30" />
          <div className="text-center">
            <p className="text-sm font-medium text-zinc-600">Integrazione lygo.it non configurata</p>
            <p className="text-xs text-zinc-400 mt-1">
              Per usare gli shortlink devi configurare l&apos;integrazione con lygo.it.
            </p>
          </div>
          <Link
            to="/integrations"
            className="px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 transition-colors"
          >
            Vai alle Integrazioni
          </Link>
        </div>
      )}

      {/* Generic error */}
      {isError && !lygoNotConfigured && (
        <div className="text-center py-24 text-red-500 text-sm">
          Errore nel caricamento degli shortlink. Aggiorna la pagina.
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-24 text-zinc-400">
          <Loader2 size={28} className="animate-spin mr-3" />
          Caricamento…
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <>
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-zinc-400 gap-4">
              <Link2 size={48} className="opacity-30" />
              <div className="text-center">
                <p className="text-sm font-medium text-zinc-600">Nessun shortlink ancora</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Crea il tuo primo shortlink cliccando su &quot;Nuovo shortlink&quot;.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-zinc-200 overflow-hidden bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-100 bg-zinc-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        Shortcode
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        URL destinazione
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        URL breve
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {items.map((link) => (
                      <tr key={link.urlCode} className="hover:bg-zinc-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-zinc-800">
                          {link.urlCode}
                        </td>
                        <td className="px-4 py-3 text-zinc-600 max-w-xs">
                          <a
                            href={link.originalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 hover:text-zinc-900 hover:underline truncate"
                            title={link.originalUrl}
                          >
                            <span className="truncate">{link.originalUrl}</span>
                            <ExternalLink size={11} className="flex-shrink-0 opacity-50" />
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <a
                              href={link.shortUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-mono truncate max-w-[160px]"
                              title={link.shortUrl}
                            >
                              {link.shortUrl}
                            </a>
                            <button
                              onClick={() => handleCopy(link)}
                              title="Copia URL breve"
                              className="flex-shrink-0 p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                            >
                              {copiedCode === link.urlCode ? (
                                <Check size={13} className="text-emerald-500" />
                              ) : (
                                <Copy size={13} />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">
                          {fmtDate(link.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEdit(link)}
                              title="Modifica"
                              className="p-1.5 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(link)}
                              title="Elimina"
                              className="p-1.5 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} className="mt-4" />
            </>
          )}
        </>
      )}

      {/* ── Create dialog ──────────────────────────────────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateUrl('');
          setCreateSlug('');
        }}
        title="Nuovo shortlink"
        description="Crea un link breve tramite lygo.it"
        size="sm"
      >
        <form onSubmit={handleCreate}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1">
                URL destinazione <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={createUrl}
                onChange={(e) => setCreateUrl(e.target.value)}
                placeholder="https://esempio.com/pagina"
                required
                className="w-full h-9 px-3 border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1">
                Slug personalizzato{' '}
                <span className="text-zinc-400 font-normal">(opzionale)</span>
              </label>
              <input
                type="text"
                value={createSlug}
                onChange={(e) => setCreateSlug(e.target.value)}
                placeholder="es. promo-estate"
                className="w-full h-9 px-3 border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
              <p className="mt-1 text-[11px] text-zinc-400">
                Se omesso, lygo.it genererà uno slug automatico.
              </p>
            </div>
          </div>
          <ModalFooter>
            <button
              type="button"
              onClick={() => {
                setCreateOpen(false);
                setCreateUrl('');
                setCreateSlug('');
              }}
              className="px-3 py-1.5 text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !createUrl.trim()}
              className="flex items-center gap-2 px-4 py-1.5 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isPending && <Loader2 size={13} className="animate-spin" />}
              Crea
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* ── Edit dialog ────────────────────────────────────────────────────── */}
      <Modal
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        title="Modifica shortlink"
        description={editTarget ? `Shortcode: ${editTarget.urlCode}` : undefined}
        size="sm"
      >
        <form onSubmit={handleEdit}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1">
                URL destinazione <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                placeholder="https://esempio.com/pagina"
                required
                className="w-full h-9 px-3 border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
            </div>
          </div>
          <ModalFooter>
            <button
              type="button"
              onClick={() => setEditTarget(null)}
              className="px-3 py-1.5 text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={editMutation.isPending || !editUrl.trim()}
              className="flex items-center gap-2 px-4 py-1.5 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {editMutation.isPending && <Loader2 size={13} className="animate-spin" />}
              Salva
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* ── Delete confirm ─────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Elimina shortlink"
        message={
          deleteTarget
            ? `Stai per eliminare il shortlink "${deleteTarget.urlCode}". Questa azione non può essere annullata.`
            : ''
        }
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.urlCode)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
